import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CategoryScale,
  Chart as ChartJS,
  Tooltip as ChartJSTooltip,
  ChartOptions,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale,
  Title,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { ChartSkeleton } from '../../../abelcommon/components/ChartSkeleton';
import { loadBalancerAPI } from '../../../api/load-balancer';
import { NetworkSummaryInfo, abelAPI } from '../../../api/abel';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartJSTooltip,
  Filler,
  Legend,
  TimeScale,
  zoomPlugin
);

enum DifficultyTrendTimeRange {
  OneMonth = '1',
  SixMonth = '6',
  All = 'all',
}

enum BlockTimeRange {
  OneWeek = '1w',
  OneMonth = '1m',
  ThreeMonths = '3m',
}

export const DifficultyTrendSection = ({
  globalNetworkInfo,
  globalNetworkInfoLoading,
  globalNetworkInfoError,
}: {
  globalNetworkInfo: NetworkSummaryInfo;
  globalNetworkInfoLoading: boolean;
  globalNetworkInfoError: AxiosError;
}) => {
  const theme = useTheme();
  const [activeChart, setActiveChart] = useState<'difficulty' | 'averageBlockTime'>('difficulty');
  const [difficultyTimeRange, setDifficultyTimeRange] = useState<DifficultyTrendTimeRange>(
    DifficultyTrendTimeRange.OneMonth
  );
  const [blockTimeRange, setBlockTimeRange] = useState<BlockTimeRange>(BlockTimeRange.OneWeek);
  const { t } = useTranslation();

  const {
    data: firstBlockDetail,
    isLoading: firstBlockDetailLoading,
    error: firstBlockError,
  } = abelAPI.hookWrapper.useBlockDetailByHeight(0, difficultyTimeRange !== DifficultyTrendTimeRange.All);

  const frequency = useMemo(() => {
    if (difficultyTimeRange === DifficultyTrendTimeRange.All) {
      if (!firstBlockDetail) return 0;
      return 30;
    }
    if (difficultyTimeRange === DifficultyTrendTimeRange.SixMonth) return 7;
    if (difficultyTimeRange === DifficultyTrendTimeRange.OneMonth) return 1;

    return 0;
  }, [difficultyTimeRange, firstBlockDetail]);

  const {
    data: blockDiffDetails,
    isLoading: blockDiffDetailsLoading,
    error: blockDiffDetailsError,
  } = loadBalancerAPI.hookWrapperQuery.useGetBlocksDifficulty(frequency, frequency === 0);

  const { options: difficultyOptions, data: difficultyData } = useMemo(() => {
    if (blockDiffDetailsLoading || !blockDiffDetails || blockDiffDetails.length === 0) return {};

    const sortedBlockDetailsByTime = blockDiffDetails.map((blockDiffDetail) => ({
      ...blockDiffDetail,
      time: new Date(blockDiffDetail.time).getTime(),
      difficulty: +blockDiffDetail.difficulty || 0,
      isSpecial: blockDiffDetail.height % 200 === 0,
    }));
    sortedBlockDetailsByTime.sort((a, b) => a.time - b.time);

    const options: ChartOptions<'line'> = {
      parsing: false,
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            color: theme.palette.text.secondary,
          },
        },
        x: {
          offset: false,
          bounds: 'data',
          type: 'time',
          ticks: {
            maxTicksLimit: 10,
            color: theme.palette.text.secondary,
          },
          time: {
            unit: 'day',
            displayFormats: {
              day: 'yyyy-MM-dd',
            },
          },
        },
      },
      plugins: {
        tooltip: {
          intersect: false,
          filter: () => true,
          callbacks: {
            label: function (context) {
              const { difficulty, height } = sortedBlockDetailsByTime[context.dataIndex];
              return [
                `${t('difficultyTrendSectionPage.block')}: ${height}`,
                `${t('difficultyTrendSectionPage.difficulty')}: ${difficulty}`,
              ];
            },
          },
        },
        legend: {
          position: 'top',
        },
        title: {
          display: true,
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'x',
          },
          pan: {
            enabled: true,
            mode: 'x',
          },
          limits: {
            x: {
              min: sortedBlockDetailsByTime[0].time,
              max: sortedBlockDetailsByTime[sortedBlockDetailsByTime.length - 1].time,
            },
          },
        },
      },
    };

    const data = {
      labels: sortedBlockDetailsByTime.map(({ time }) => time),
      datasets: [
        {
          fill: true,
          label: t('difficultyTrendSectionPage.difficulty'),
          data: sortedBlockDetailsByTime.map(({ difficulty, time, isSpecial }) => ({
            x: time,
            y: difficulty,
            r: isSpecial ? 5 : 0,
          })),
          borderColor: theme.palette.appBlue.lightest,
          backgroundColor: 'rgb(37,150,190, 0.5)',
          pointRadius: sortedBlockDetailsByTime.map(({ isSpecial }) => (isSpecial ? 3 : 0)),
          pointBackgroundColor: '#FFFFFF',
          pointBorderColor: theme.palette.appBlue.lightest,
        },
      ],
    };

    return {
      options,
      data,
    };
  }, [blockDiffDetailsLoading, blockDiffDetails, theme.palette.text.secondary, theme.palette.appBlue.lightest, t]);

  const {
    data: averageBlockTimeData,
    isLoading: averageBlockTimeLoading,
    error: averageBlockTimeError,
  } = loadBalancerAPI.hookWrapperQuery.useGetAverageBlockTime(blockTimeRange);

  const { options: avgBlockTimeOptions, data: avgBlockTimeData } = useMemo(() => {
    if (averageBlockTimeLoading || !averageBlockTimeData) return {};

    const sortedBlockTimeData = averageBlockTimeData.map((blockTime) => {
      const meanTime = blockTime.meanTime ? new Date(blockTime.meanTime).getTime() : null;

      return {
        ...blockTime,
        time: meanTime,
        averageBlockTime: parseInt(blockTime.averageBlockTime as unknown as string),
      };
    });

    const filteredBlockTimeData = sortedBlockTimeData.filter((blockTime) => blockTime.time !== null);

    filteredBlockTimeData.sort((a, b) => a.time - b.time);

    const options: ChartOptions<'line'> = {
      parsing: false,
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            color: theme.palette.text.secondary,
            stepSize: 100,
          },
          suggestedMax: 1000,
        },
        x: {
          bounds: 'data',
          type: 'time',
          offset: false,
          ticks: {
            maxTicksLimit: 10,
            color: theme.palette.text.secondary,
          },
          time: {
            unit: 'day',
            displayFormats: {
              day: 'yyyy-MM-dd',
            },
          },
        },
      },
      plugins: {
        tooltip: {
          intersect: false,
          filter: () => true,
          callbacks: {
            label: function (context) {
              const { averageBlockTime, startHeight, endHeight } = filteredBlockTimeData[context.dataIndex];
              return [
                `${t('difficultyTrendSectionPage.blockRange')}: ${startHeight} - ${endHeight}`,
                `${t('difficultyTrendSectionPage.averageBlockTime')}: ${averageBlockTime}`,
              ];
            },
          },
        },
        legend: {
          position: 'top',
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'x',
          },
          pan: {
            enabled: true,
            mode: 'x',
          },
          limits: {
            x: {
              min: filteredBlockTimeData[0]?.time,
              max: filteredBlockTimeData[filteredBlockTimeData.length - 1]?.time,
            },
          },
        },
      },
    };

    const data = {
      labels: filteredBlockTimeData.map(({ time }) => time),
      datasets: [
        {
          fill: true,
          lineTension: 0.3,
          label: t('difficultyTrendSectionPage.averageBlockTime'),
          data: filteredBlockTimeData.map(({ averageBlockTime, time }) => ({
            x: time,
            y: averageBlockTime,
          })),
          borderColor: theme.palette.appBlue.lightest,
          backgroundColor: 'rgb(37,150,190, 0.5)',
          pointRadius: 3,
          pointHoverRadius: 7,
          pointBackgroundColor: '#FFFFFF',
          pointBorderColor: theme.palette.appBlue.lightest,
        },
      ],
    };

    return {
      options,
      data,
    };
  }, [theme, averageBlockTimeData, averageBlockTimeLoading, t]);

  const difficultyChartFilterButtonProps = useMemo(
    () => [
      {
        value: DifficultyTrendTimeRange.OneMonth,
        label: '1M',
      },
      {
        value: DifficultyTrendTimeRange.SixMonth,
        label: '6M',
      },
      {
        value: DifficultyTrendTimeRange.All,
        label: 'All',
      },
    ],
    []
  );

  const blockTimeChartFilterButtonProps = [
    { value: BlockTimeRange.OneWeek, label: '1W' },
    { value: BlockTimeRange.OneMonth, label: '1M' },
    { value: BlockTimeRange.ThreeMonths, label: '3M' },
  ];

  const apiLoading =
    globalNetworkInfoLoading ||
    (activeChart === 'difficulty'
      ? blockDiffDetailsLoading || (difficultyTimeRange === DifficultyTrendTimeRange.All && firstBlockDetailLoading)
      : averageBlockTimeLoading);

  const apiError =
    globalNetworkInfoError ||
    (activeChart === 'difficulty'
      ? blockDiffDetailsError || (difficultyTimeRange === DifficultyTrendTimeRange.All && firstBlockError)
      : averageBlockTimeError);

  return (
    <Card>
      <CardHeader
        title={
          <ToggleButtonGroup
            color="primary"
            value={activeChart}
            exclusive
            onChange={(e, newActiveChart) => {
              if (newActiveChart !== null) {
                setActiveChart(newActiveChart);
              }
            }}
          >
            <ToggleButton value="difficulty">
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t('difficultyTrendSectionPage.difficultyTrend')}
              </Typography>
            </ToggleButton>
            <ToggleButton value="averageBlockTime">
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t('difficultyTrendSectionPage.averageBlockTime')}
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        }
        action={
          <ToggleButtonGroup
            color="primary"
            value={activeChart === 'difficulty' ? difficultyTimeRange : blockTimeRange}
            exclusive
            onChange={(e, newTimeRange) => {
              if (newTimeRange !== null) {
                if (activeChart === 'difficulty') {
                  setDifficultyTimeRange(newTimeRange);
                } else {
                  setBlockTimeRange(newTimeRange);
                }
              }
            }}
          >
            {(activeChart === 'difficulty' ? difficultyChartFilterButtonProps : blockTimeChartFilterButtonProps).map(
              ({ value, label }) => (
                <ToggleButton key={label} value={value}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {label}
                  </Typography>
                </ToggleButton>
              )
            )}
          </ToggleButtonGroup>
        }
      />
      <CardContent sx={{ overflow: 'auto', height: '500px' }}>
        {apiError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {apiError?.message}
          </Alert>
        ) : apiLoading ? (
          <ChartSkeleton />
        ) : activeChart === 'difficulty' ? (
          <Line options={difficultyOptions} data={difficultyData} />
        ) : (
          <Line options={avgBlockTimeOptions} data={avgBlockTimeData} />
        )}
      </CardContent>
    </Card>
  );
};
